import isUndefined from 'lodash/isUndefined';
import size from 'lodash/size';
import React, {Fragment, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import loadable from '@loadable/component';
import classNames from 'classnames';
import IconFilter from '@spothero/icons/filter';
import Badge from '@spothero/ui/badge';
import Button from '@spothero/ui/button';
import Tooltip from '@spothero/ui/tooltip';
import AmenityUtils from '@spothero/utils/amenity';
import StorageUtils from '@spothero/utils/storage';
import {getSpots} from 'spots/spots-selectors';
import {resetFilters, updateFilters} from './filters-actions';
import {getAmenities, getReservationTypes} from './filters-selectors';
import FilterItem from './FilterItem';

const AvailabilityFilters = loadable(() => import('./AvailabilityFilters'));
const FiltersModal = loadable(() => import('./FiltersModal'));
const TOUCHLESS_COOKIE = 'sh-touchless-tooltip-disabled';

const QuickFilters = ({
    className,
    showReservationTypes,
    visibleSlugs,
    allowTouchlessTooltip,
}) => {
    const dispatch = useDispatch();
    const [showModal, setShowModal] = useState(false);
    const [showTouchlessTooltip, setShowTouchlessTooltip] = useState(() => {
        const hideTouchlessTooltip =
            StorageUtils.get(TOUCHLESS_COOKIE) || false;

        return allowTouchlessTooltip && !hideTouchlessTooltip;
    });
    const {
        filteredSpotsNum: numSpots,
        numActive: numActiveFilters,
        currentAmenities: activeAmenities,
        allAmenities: amenities,
        resTypes: reservationTypes,
        newActiveReservationType: activeReservationType,
    } = useSelector(state => {
        const resTypes = getReservationTypes(state);
        const allAmenities = getAmenities(state);
        const {
            filters: {
                numActive,
                activeAmenities: currentAmenities,
                activeReservationType: activeResType,
            },
        } = state;
        let newActiveReservationType = activeResType;

        // check if reservation type is valid
        if (
            newActiveReservationType !== 'all' &&
            isUndefined(resTypes[newActiveReservationType])
        ) {
            newActiveReservationType = 'all';
        }

        return {
            numActive,
            allAmenities,
            currentAmenities,
            resTypes,
            newActiveReservationType,
            filteredSpotsNum: getSpots(state).length,
        };
    });
    const badgeLabel =
        numActiveFilters > 0 ? numActiveFilters.toString() : null;
    const classes = classNames('QuickFilters', className);
    const onAmenityChange = (amenity, isChecked, isQuickFilter = false) => {
        let newActiveAmenities = [...activeAmenities];

        if (isChecked) {
            newActiveAmenities.push(amenity);
        } else {
            newActiveAmenities = newActiveAmenities.filter(
                item => item !== amenity
            );
        }

        dispatch(
            updateFilters({
                activeAmenities: newActiveAmenities,
                viaQuickFilter: isQuickFilter,
            })
        );
    };
    const onClearFilters = () => {
        dispatch(resetFilters());
    };
    const onReservationTypeChange = value => {
        dispatch(
            updateFilters({
                activeReservationType: value,
            })
        );
    };
    const onModalToggle = evt => {
        setShowModal(show => !show);
    };
    const onTouchlessTooltipHidden = evt => {
        setShowTouchlessTooltip(false);

        StorageUtils.set(TOUCHLESS_COOKIE, true);
    };

    return (
        <div className={classes}>
            {!showReservationTypes &&
                Boolean(size(amenities)) &&
                visibleSlugs.length > 0 && (
                    <div className="QuickFilters-items">
                        {visibleSlugs.map(slug => {
                            const amenity = amenities[slug];

                            if (!amenity) {
                                return null;
                            }

                            // if no filters are active and this amenity doesn't apply to the current search, hide it
                            // so that it does not show up as a disabled amenity when it really has no business being there
                            const isHidden =
                                numActiveFilters === 0 && amenity.count === 0;
                            const isTouchless = slug === 'touchless';
                            const touchlessId = 'QuickFilter-touchless';

                            if (isHidden) {
                                return null;
                            }

                            return (
                                <Fragment key={amenity.slug}>
                                    <FilterItem
                                        {...(isTouchless && {
                                            id: touchlessId,
                                        })}
                                        slug={amenity.slug}
                                        label={amenity.name}
                                        defaultChecked={activeAmenities.includes(
                                            amenity.slug
                                        )}
                                        disabled={amenity.count === 0}
                                        isQuickFilter
                                        onChange={onAmenityChange}
                                    />
                                    {isTouchless && showTouchlessTooltip && (
                                        <Tooltip
                                            className="QuickFilter-tooltip-touchless"
                                            triggerSelector={`#${touchlessId}`}
                                            position="bottom"
                                            updateOnMove
                                            onHidden={onTouchlessTooltipHidden}
                                        >
                                            <p>
                                                Touchless Redemption
                                                <span>New</span>
                                            </p>
                                            <p>
                                                You can now filter to find
                                                facilities that don&apos;t
                                                require touching buttons or
                                                surfaces to redeem your
                                                reservation.
                                            </p>
                                        </Tooltip>
                                    )}
                                </Fragment>
                            );
                        })}
                    </div>
                )}
            {showReservationTypes && (
                <AvailabilityFilters
                    activeReservationType={activeReservationType}
                    reservationTypes={reservationTypes}
                    onChange={onReservationTypeChange}
                />
            )}
            <Button className="QuickFilters-filter" onClick={onModalToggle}>
                {badgeLabel ? (
                    <Badge className="QuickFilters-filter-badge">
                        {badgeLabel}
                    </Badge>
                ) : (
                    <IconFilter />
                )}
                Filter
            </Button>
            {showModal && (
                <FiltersModal
                    numSpots={numSpots}
                    amenities={amenities}
                    activeAmenities={activeAmenities}
                    numActiveFilters={numActiveFilters}
                    onChange={onAmenityChange}
                    onClear={onClearFilters}
                    onHidden={onModalToggle}
                />
            )}
        </div>
    );
};

QuickFilters.propTypes = {
    className: PropTypes.string,
    showReservationTypes: PropTypes.bool,
    visibleSlugs: PropTypes.arrayOf(
        PropTypes.oneOf(Object.keys(AmenityUtils.ICONS))
    ),
    allowTouchlessTooltip: PropTypes.bool,
};

QuickFilters.defaultProps = {
    visibleSlugs: [],
};

export default QuickFilters;
